import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, Gift } from "lucide-react";
import { PageHeader, Loader } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DndBoard, PrizeBoard } from "@/components/dnd/DndBoard";
import { DndOverlays } from "@/components/dnd/DndOverlays";
import { DndHistory } from "@/components/dnd/DndHistory";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtPKR } from "@/lib/format";

const HINT = { pick_own: "Choose your personal box", opening: "Open a box", offer: "Banker Offer!", final_swap: "Final decision", completed: "Game over" };

const StartPanel = ({ st, onStart, busy }) => {
  const [amount, setAmount] = useState(String(st.min_bet));
  return (
    <div className="ec-card p-6" data-testid="dnd-start-panel">
      <div className="flex flex-wrap gap-x-3 gap-y-1 justify-between text-xs text-gray-500 mb-3">
        <span data-testid="dnd-limits">Min {fmtPKR(st.min_bet)} · Max {fmtPKR(st.max_bet)}</span>
        <span data-testid="dnd-balance">Balance {fmtPKR(st.balance)}</span>
      </div>
      <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-12 text-lg" disabled={!st.enabled} data-testid="dnd-amount-input" />
      <Button onClick={() => onStart(Number(amount))} disabled={busy || !st.enabled} className="w-full h-12 mt-4 btn-glow font-head font-bold" data-testid="dnd-start-button">START GAME</Button>
      <ol className="list-decimal ps-4 space-y-1.5 text-xs text-gray-500 mt-5" data-testid="dnd-rules">{st.rules.map((x, i) => <li key={i}>{x}</li>)}</ol>
    </div>
  );
};

export default function DealOrNoDeal() {
  const { user, setUser } = useAuth();
  const [st, setSt] = useState(null);
  const [hist, setHist] = useState([]);
  const [game, setGame] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [s, h] = await Promise.all([api.get("/dnd/state"), api.get("/dnd/history")]);
    setSt(s.data); setHist(h.data); setUser({ ...user, balance: s.data.balance });
    if (s.data.game) setGame(s.data.game);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  if (!st) return <Loader />;

  const call = async (fn, after) => {
    setBusy(true);
    try { const { data } = await fn(); setGame(data); if (after) await after(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(false);
  };
  const start = (amount) => {
    if (!amount || amount < st.min_bet) { toast.error(`Minimum participation is ${fmtPKR(st.min_bet)}.`); return; }
    if (amount > st.max_bet) { toast.error(`Maximum participation is ${fmtPKR(st.max_bet)}.`); return; }
    call(() => api.post("/dnd/start", { amount }), () => { load(); });
  };
  const refreshHist = () => api.get("/dnd/history").then(({ data }) => setHist(data)).catch(() => {});
  const onBox = (n) => call(() => api.post(`/dnd/${game.id}/${game.status === "pick_own" ? "pick" : "open"}`, { box: n }), refreshHist);
  const refresh = (d) => (d.status === "completed" ? load() : refreshHist());
  const onDecision = (deal) => call(() => api.post(`/dnd/${game.id}/decision`, { deal }), refresh);
  const onFinal = (swap) => call(() => api.post(`/dnd/${game.id}/final`, { swap }), refresh);

  return (
    <div>
      <PageHeader title="Deal or No Deal" subtitle="20 boxes, one is yours. Open the rest, face the Banker, and decide: DEAL or NO DEAL?" />
      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 ec-card ec-dark relative p-4 sm:p-6 overflow-hidden" data-testid="dnd-stage">
          {!game ? (
            <div className="min-h-[22rem] flex flex-col items-center justify-center text-center" data-testid="dnd-idle">
              {st.enabled ? <Gift className="w-14 h-14 text-[#E8C766] mb-3 animate-floaty" /> : <Ban className="w-12 h-12 text-gray-500 mb-3" />}
              <p className="font-head text-3xl font-extrabold text-white">{st.enabled ? "Ready to play?" : "Game Closed"}</p>
              <p className="text-gray-400 text-sm mt-2 max-w-sm">{st.enabled ? "Enter your amount and press Start. The 20 prizes are shuffled and locked the moment your game begins." : "Deal or No Deal is currently disabled by admin."}</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4 gap-2">
                <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border border-[rgba(232,199,102,0.5)] text-[#E8C766]" data-testid="dnd-phase">{HINT[game.status]}</span>
                <span className="text-xs text-gray-400 keep-ltr" data-testid="dnd-progress">#{game.game_no} · {fmtPKR(game.amount)} · opened {game.opened_count}/18</span>
              </div>
              <DndBoard game={game} onBox={busy ? () => {} : onBox} />
              <p className="text-[10px] text-gray-600 truncate keep-ltr mt-4" data-testid="dnd-commit-hash">Commitment: {game.commit_hash}</p>
              <DndOverlays game={game} busy={busy} onDecision={onDecision} onFinal={onFinal} onAgain={() => setGame(null)} />
            </>
          )}
        </div>
        <div className="space-y-4">
          {!game && <StartPanel st={st} onStart={start} busy={busy} />}
          <div className="ec-card ec-dark p-5" data-testid="dnd-prizes-card">
            <p className="text-sm font-semibold text-white mb-3">{game ? "Prizes still in play" : "Prizes in the boxes"}</p>
            <PrizeBoard prizes={game ? game.prizes : st.prizes} remaining={game?.remaining_prizes} />
          </div>
        </div>
      </div>
      <div className="mt-6"><DndHistory rows={hist} /></div>
    </div>
  );
}
